import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import BrandLogo from './BrandLogo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/chat';
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
    const payload = isLogin ? { email, password } : { name, email, password };

    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Something went wrong');
        setLoading(false);
        return;
      }

      localStorage.setItem('opensov-token', data.token);
      localStorage.setItem('opensov-user', JSON.stringify(data.user));
      navigate(redirectTo);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isDashboardRedirect = redirectTo.includes('dashboard');

  return (
    <div className="min-h-screen bg-app flex" data-testid="auth-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center bg-app-alt">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(16,185,129,0.08)_0%,_transparent_70%)]" />
        <div className="relative z-10 max-w-md px-12">
          <BrandLogo className="h-14 mb-8" />
          <h1 className="font-heading text-4xl font-bold text-content tracking-tight mb-4">
            {isDashboardRedirect ? 'Developer Platform' : "India's Sovereign AI"}
          </h1>
          <p className="text-content-2 text-base leading-relaxed mb-8">
            {isDashboardRedirect
              ? 'Access your API keys, monitor usage, and integrate OpenSov AI into your applications.'
              : 'Build with the most advanced AI platform designed for India. Fast, secure, and sovereign.'}
          </p>
          <div className="space-y-3">
            {(isDashboardRedirect
              ? [
                  { text: 'OpenAI-compatible API', sub: 'Drop-in replacement for any OpenAI SDK' },
                  { text: 'Streaming support', sub: 'Real-time SSE responses for production apps' },
                  { text: '100 requests/day free', sub: 'Start building immediately, no credit card' },
                ]
              : [
                  { text: 'Gemini 3 Flash powered', sub: 'Lightning-fast responses' },
                  { text: 'Voice input & output', sub: 'Talk naturally with AI' },
                  { text: 'Developer API', sub: 'Build AI-powered applications' },
                ]
            ).map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                <div>
                  <p className="text-content text-sm font-medium">{item.text}</p>
                  <p className="text-content-3 text-xs">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-8 left-12 text-xs text-content-3 font-mono">opensov.com</div>
      </div>

      {/* Right panel - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <BrandLogo className="h-10 mx-auto mb-3" />
            <p className="text-content-3 text-sm">India's Sovereign AI</p>
          </div>

          <h2 className="text-2xl font-heading font-bold text-content mb-1 tracking-tight" data-testid="auth-title">
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="text-content-2 text-sm mb-8">
            {isLogin
              ? isDashboardRedirect ? 'Sign in to access your developer dashboard' : 'Sign in to continue to OpenSov AI'
              : 'Get started with OpenSov AI'}
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1.5">Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  required={!isLogin} placeholder="Your name"
                  className="w-full px-3.5 py-2.5 rounded-md bg-app border border-line-2 text-content text-sm placeholder-content-3 focus:outline-none focus:border-[#10B981]/50 focus:ring-1 focus:ring-[#10B981]/20 transition-all"
                  data-testid="auth-name-input" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-content-2 mb-1.5">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="you@example.com"
                className="w-full px-3.5 py-2.5 rounded-md bg-app border border-line-2 text-content text-sm placeholder-content-3 focus:outline-none focus:border-[#10B981]/50 focus:ring-1 focus:ring-[#10B981]/20 transition-all"
                data-testid="auth-email-input" />
            </div>

            <div>
              <label className="block text-sm font-medium text-content-2 mb-1.5">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  required minLength={6} placeholder="Min 6 characters"
                  className="w-full px-3.5 py-2.5 rounded-md bg-app border border-line-2 text-content text-sm placeholder-content-3 focus:outline-none focus:border-[#10B981]/50 focus:ring-1 focus:ring-[#10B981]/20 transition-all pr-12"
                  data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-content-3 hover:text-content-2 transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-2.5 rounded-md bg-emerald-500 text-btn-foreground font-semibold text-sm hover:bg-emerald-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2 mt-2 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
              data-testid="auth-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>{isLogin ? 'Sign In' : 'Create Account'}<ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-sm text-content-2 hover:text-emerald-500 transition-colors"
              data-testid="auth-toggle-btn">
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>

          <button onClick={() => navigate('/')}
            className="mt-8 mx-auto block text-xs text-content-3 hover:text-content-2 transition-colors"
            data-testid="auth-back-btn">
            Back to OpenSov
          </button>
        </div>
      </div>
    </div>
  );
}
